import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { ShieldAlert } from "lucide-react";

import { hostelService, type Allocation, type WelfareCase } from "../../services/hostelService";
import { apiErrorMessage } from "../../utils/apiError";
import { daysFromTodayIso, nowLocalInput, todayIso } from "../../utils/dates";
import AIAssistField from "../AIAssistField";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ReasonPanel, errorLine } from "../inventory/InventoryParts";
import { SearchPick } from "../library/Pickers";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { ActionPanel, DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { SEVERITIES, VIOLATION_TYPES, clock, day, labelFrom, severityDot, welfareConfig, welfareStatus } from "./hostelLogic";

function ReportForm({ boarders, onClose, onDone }: { boarders: Allocation[]; onClose: () => void; onDone: (row: WelfareCase) => void }) {
  const [boarderId, setBoarderId] = useState("");
  const [kind, setKind] = useState("curfew");
  const [severity, setSeverity] = useState("minor");
  const [date, setDate] = useState(todayIso());
  const [time, setTime] = useState(nowLocalInput().slice(11, 16));
  const [description, setDescription] = useState("");
  const boarder = boarders.find((row) => String(row.id) === boarderId);
  const save = useMutation({ mutationFn: hostelService.reportWelfareCase, onSuccess: onDone });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!boarder) return;
    const data = new FormData(event.currentTarget);
    save.mutate({
      student: boarder.student,
      hostel: boarder.hostel,
      violation_type: kind,
      severity,
      incident_date: date,
      incident_time: time,
      description: description.trim(),
      witnesses: String(data.get("witnesses") ?? "").trim() || null,
    });
  };
  return (
    <FormModal
      id="welfare-case"
      title="Record a welfare case"
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The case could not be recorded.") : undefined}
      saveLabel="Record it"
      note="The matron decides the action. Families can read their own child's cases."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The boarder" divided={false}>
        <Field label="Boarder" as="div" className="sm:col-span-2">
          <SearchPick
            items={boarders}
            value={boarderId}
            onChange={setBoarderId}
            label={(row) => row.student_name}
            detail={(row) => `${row.hostel_name} · room ${row.room_number}`}
            text={(row) => `${row.student_name} ${row.student_id}`}
            placeholder="Search by boarder's name"
            empty="No boarder matches"
            required
          />
        </Field>
      </FormSection>
      <FormSection title="What happened">
        <Field label="Kind">
          <select value={kind} onChange={(event) => setKind(event.target.value)} className="input mt-1">
            {VIOLATION_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="How serious">
          <select value={severity} onChange={(event) => setSeverity(event.target.value)} className="input mt-1">
            {SEVERITIES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="Date">
          <input required type="date" max={todayIso()} value={date} onChange={(event) => setDate(event.target.value)} className="input mt-1" />
        </Field>
        <Field label="Time">
          <input required type="time" value={time} onChange={(event) => setTime(event.target.value)} className="input mt-1" />
        </Field>
        <Field label="Describe it" as="div" className="sm:col-span-2">
          <AIAssistField
            rows={3}
            value={description}
            onChange={(next) => setDescription(next)}
            placeholder="What happened, plainly and without judgement."
            context={{ key: "hostel.note", params: { kind: "welfare_description", violation_type: kind, severity, incident_date: date, incident_time: time } }}
            draftLabel="Help me write it"
            draftHint="Jot the facts; it writes them up calmly and never adds any. It never sees the boarder's name."
          />
        </Field>
        <Field label="Who saw it" optional className="sm:col-span-2">
          <input name="witnesses" className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Boarders' welfare and conduct: what happened, what was done, and how it ended. */
export function WelfarePanel({ cases, boarders, isLoading, isError, onRetry, canManage, canReport, isFamily, onChanged }: {
  cases?: WelfareCase[];
  boarders: Allocation[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  canReport: boolean;
  isFamily: boolean;
  onChanged: () => void;
}) {
  const today = todayIso();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [action, setAction] = useState("");
  const [sanctions, setSanctions] = useState("");
  const [warning, setWarning] = useState(false);
  const [followUp, setFollowUp] = useState(false);
  const [followDate, setFollowDate] = useState(daysFromTodayIso(7));
  const done = (text: string, close = false) => {
    setNotice({ tone: "success", text });
    if (close) setViewing(null);
    onChanged();
  };
  const act = useMutation({ mutationFn: hostelService.takeWelfareAction, onSuccess: (row) => done(`The action on ${row.student_name}'s case is recorded.`) });
  const resolve = useMutation({ mutationFn: hostelService.resolveWelfareCase, onSuccess: (row) => done(`${row.student_name}'s case is resolved.`, true) });
  const active = boarders.filter((row) => row.status === "active").sort((a, b) => a.student_name.localeCompare(b.student_name));

  const columns: RegisterColumn<WelfareCase>[] = [
    { header: "Date", cell: (row) => day(row.incident_date), exportValue: (row) => day(row.incident_date), className: "whitespace-nowrap tabular-nums text-gray-700" },
    { header: "Boarder", cell: (row) => <span className="font-medium text-gray-900">{row.student_name}</span>, exportValue: (row) => row.student_name },
    { header: "Kind", cell: (row) => labelFrom(VIOLATION_TYPES, row.violation_type), exportValue: (row) => labelFrom(VIOLATION_TYPES, row.violation_type), className: "text-gray-700" },
    { header: "How serious", cell: (row) => <StatusDot label={labelFrom(SEVERITIES, row.severity)} dot={severityDot(row.severity)} />, exportValue: (row) => labelFrom(SEVERITIES, row.severity) },
    { header: "What happened", cell: (row) => row.description, exportValue: (row) => row.description, className: "min-w-[14rem] max-w-md whitespace-normal text-gray-700" },
    { header: "Residence", cell: (row) => row.hostel_name, exportValue: (row) => row.hostel_name, className: "text-gray-700" },
    { header: "Status", cell: (row) => <StatusDot {...welfareStatus(row, today)} />, exportValue: (row) => welfareStatus(row, today).label },
  ];

  const item = viewing !== null ? cases?.find((row) => row.id === viewing) : undefined;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Welfare"
        description={canManage ? "Open a case to record what was done and close it." : isFamily ? "Anything recorded about your child in the residence, and how it was dealt with." : "Cases you have recorded."}
        actions={canReport && (
          <button type="button" disabled={!active.length} onClick={() => setAdding(true)} className="btn-primary h-8 min-h-8 px-3 text-xs disabled:opacity-50">
            <ShieldAlert className="mr-1.5 h-3.5 w-3.5" /> Record a case
          </button>
        )}
        rows={cases}
        config={welfareConfig(today)}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          act.reset();
          resolve.reset();
          setAction("");
          setSanctions("");
          setWarning(false);
          setFollowUp(false);
          setViewing(row.id);
        }}
        openLabel={(row) => (canManage && !row.is_resolved ? "Deal with it" : "Open")}
        noun={["case", "cases"]}
        exportName={{ file: "boarding-welfare", title: "Boarding Welfare Cases" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={isFamily ? "Nothing has been recorded." : "No welfare cases recorded."}
        canExport={!isFamily}
      />

      {item && (
        <DetailsModal
          title={`${item.student_name} · ${labelFrom(VIOLATION_TYPES, item.violation_type)}`}
          headline={{ label: labelFrom(SEVERITIES, item.severity), value: `${day(item.incident_date)} ${clock(item.incident_time)}`, note: `${item.hostel_name} · recorded by ${item.reported_by_name}` }}
          status={welfareStatus(item, today)}
          facts={[
            ["Warning given", item.action_taken ? (item.warning_issued ? "Yes" : "No") : undefined],
            ["Sent home for", item.suspension_days ? `${item.suspension_days} ${item.suspension_days === 1 ? "day" : "days"}` : undefined],
            ["Action by", item.action_taken_by_name],
            ["Follow-up on", item.follow_up_required ? day(item.follow_up_date) : undefined],
            ["Resolved", item.is_resolved ? `${day(item.resolution_date)} by ${item.resolved_by_name}` : undefined],
          ]}
          sections={[
            { title: "What happened", body: item.description },
            { title: "Who saw it", body: item.witnesses },
            { title: "Action taken", body: item.action_taken },
            { title: "Sanctions", body: item.sanctions },
            { title: "Follow-up", body: item.follow_up_notes },
            { title: "How it was resolved", body: item.resolution_notes },
          ]}
          onClose={() => setViewing(null)}
        >
          {canManage && !item.is_resolved && (
            <ActionPanel title={item.action_taken ? "Update the action" : "Record the action"} description="What was done about it, in words the family can read.">
              <Field label="Action taken" as="div">
                <AIAssistField
                  rows={2}
                  value={action}
                  onChange={(next) => setAction(next)}
                  placeholder="e.g. Talked with her and the class teacher; she apologised."
                  context={{ key: "hostel.note", params: { kind: "welfare_action", id: item.id } }}
                  draftLabel="Draft it"
                  draftHint="Jot what was done; it writes it up without adding anything."
                />
              </Field>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Sanctions" optional hint="e.g. Extra cleaning duty for a week.">
                  <input value={sanctions} onChange={(event) => setSanctions(event.target.value)} className="input mt-1" />
                </Field>
                <label className="flex items-center gap-2 self-end pb-2 text-sm font-medium text-gray-700">
                  <input type="checkbox" checked={warning} onChange={(event) => setWarning(event.target.checked)} className="h-4 w-4 rounded border-gray-300" /> A warning was given
                </label>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input type="checkbox" checked={followUp} onChange={(event) => setFollowUp(event.target.checked)} className="h-4 w-4 rounded border-gray-300" /> Check again later
                </label>
                {followUp && (
                  <Field label="Check on">
                    <input type="date" min={todayIso()} value={followDate} onChange={(event) => setFollowDate(event.target.value)} className="input mt-1" />
                  </Field>
                )}
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  disabled={!action.trim() || act.isPending}
                  onClick={() => act.mutate({ id: item.id, action_taken: action.trim(), sanctions: sanctions.trim() || undefined, warning_issued: warning, follow_up_required: followUp, follow_up_date: followUp ? followDate : undefined })}
                  className="btn btn-outline"
                >
                  Save the action
                </button>
              </div>
              {act.isError && errorLine(act.error, "The action could not be saved.")}
            </ActionPanel>
          )}
          {canManage && !item.is_resolved && item.action_taken && (
            <ReasonPanel
              title="Resolve the case"
              label="How it ended"
              placeholder="e.g. No further incidents in the following month."
              context={{ key: "hostel.note", params: { kind: "welfare_resolution", id: item.id } }}
              button="Resolve"
              busy={resolve.isPending}
              error={resolve.error}
              errorText="The case could not be resolved."
              onSubmit={(notes) => resolve.mutate({ id: item.id, resolution_notes: notes })}
            />
          )}
        </DetailsModal>
      )}

      {adding && (
        <ReportForm
          boarders={active}
          onClose={() => setAdding(false)}
          onDone={(saved) => {
            setAdding(false);
            done(`The case about ${saved.student_name} is recorded.`);
            setViewing(saved.id);
          }}
        />
      )}
    </div>
  );
}
